import { useMemo, useState, type ReactNode } from "react";
import { Chip } from "../../../components/ui/Chip";
import { BaseCodeSelect } from "./BaseCodeSelect";
import { readsAsLookup } from "./designationParts";
import { sharedCodes } from "./registryFilter";
import {
  designationCountByBaseCode,
  filterByBaseCode,
  filterDesignations,
  groupByResinFamily,
  NO_RESIN_FAMILY,
  type ResinFamilyGroup,
} from "./resinGroups";
import { ResinTable } from "./ResinTable";
import { SharedCodesTable } from "./SharedCodesTable";
import type { ResinFamily } from "../types";
import type { NomenclatureDatabase } from "./useNomenclatureDatabase";

interface Props {
  resinFamilies: ResinFamily[];
  database: NomenclatureDatabase;
  canWrite: boolean;
}

const SHARED_CODES_VIEW = "SHARED_CODES";

const TOOLBAR_CLASSES =
  "sticky top-[var(--page-header-h,112px)] z-10 -mx-[22px] -mt-[22px] flex flex-col gap-3.5 rounded-t-lg border-b border-rs-jet bg-rs-off-black px-[22px] pt-4.5 pb-3.5 max-[1000px]:static max-[1000px]:m-0 max-[1000px]:border-0 max-[1000px]:p-0";

function plural(count: number, singular: string, pluralWord = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralWord}`;
}

function pickerButtonClasses(isSelected: boolean): string {
  return `inline-flex items-baseline gap-1.75 rounded-md border px-3.25 py-1.75 text-sm font-semibold cursor-pointer ${
    isSelected
      ? "border-rs-green bg-rs-green text-rs-black"
      : "border-[#3c4235] bg-transparent text-rs-off-white hover:border-rs-green hover:text-rs-green"
  }`;
}

export function ResinDatabase({ resinFamilies, database, canWrite }: Props) {
  const { designations, gradeLinks, approvedCodes, loadError, reloadGrades: handleTdsChanged } = database;
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [baseCodeByResin, setBaseCodeByResin] = useState<Record<string, string | null>>({});
  const [searchTextByResin, setSearchTextByResin] = useState<Record<string, string>>({});

  const groups = useMemo(
    () =>
      designations && gradeLinks && approvedCodes
        ? groupByResinFamily(designations, gradeLinks, resinFamilies, approvedCodes)
        : null,
    [designations, gradeLinks, approvedCodes, resinFamilies],
  );
  const readsAsByCode = useMemo(() => readsAsLookup(approvedCodes ?? []), [approvedCodes]);

  if (loadError) return <p className="m-0 text-rs-light-gray text-[13px]">Couldn't load the database: {loadError}</p>;
  if (!groups || !approvedCodes) return <p className="m-0 text-rs-light-gray text-[13px]">Loading…</p>;

  const activeView =
    selectedView === SHARED_CODES_VIEW || groups.some((group) => group.resinFamilyCode === selectedView)
      ? selectedView
      : (groups[0]?.resinFamilyCode ?? SHARED_CODES_VIEW);
  const selectedGroup = groups.find((group) => group.resinFamilyCode === activeView) ?? null;
  const sharedCodeCount = sharedCodes(approvedCodes).length;

  const picker = (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Resin type">
      {groups.map((group) => (
        <button
          key={group.resinFamilyCode}
          type="button"
          aria-pressed={activeView === group.resinFamilyCode}
          className={pickerButtonClasses(activeView === group.resinFamilyCode)}
          onClick={() => setSelectedView(group.resinFamilyCode)}
        >
          {group.resinFamilyCode === NO_RESIN_FAMILY ? group.displayName : group.resinFamilyCode}
          <span className="text-xs font-medium opacity-75">{group.designations.length}</span>
        </button>
      ))}
      <span className="mx-1 w-px self-stretch bg-rs-jet" aria-hidden="true" />
      <button
        type="button"
        aria-pressed={activeView === SHARED_CODES_VIEW}
        className={pickerButtonClasses(activeView === SHARED_CODES_VIEW)}
        onClick={() => setSelectedView(SHARED_CODES_VIEW)}
      >
        Shared Codes
        <span className="text-xs font-medium opacity-75">{sharedCodeCount}</span>
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-3.5 min-w-0">
      {selectedGroup ? (
        <ResinView
          picker={picker}
          group={selectedGroup}
          selectedBaseCode={baseCodeByResin[selectedGroup.resinFamilyCode] ?? null}
          searchText={searchTextByResin[selectedGroup.resinFamilyCode] ?? ""}
          onSelectBaseCode={(baseCode) =>
            setBaseCodeByResin((current) => ({ ...current, [selectedGroup.resinFamilyCode]: baseCode }))
          }
          onSearch={(searchText) =>
            setSearchTextByResin((current) => ({ ...current, [selectedGroup.resinFamilyCode]: searchText }))
          }
          readsAsByCode={readsAsByCode}
          canWrite={canWrite}
          onTdsChanged={handleTdsChanged}
        />
      ) : (
        <>
          <div className={TOOLBAR_CLASSES}>
            {picker}
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="m-0 text-base font-semibold text-white">Shared Codes</h3>
              <span className="text-[13px] text-[#a9b0a1]">Fillers, additives, color and recycle · used by every resin</span>
              <Chip variant={sharedCodeCount ? "ok" : "default"}>{plural(sharedCodeCount, "code")}</Chip>
            </div>
          </div>
          <SharedCodesTable approvedCodes={approvedCodes} />
        </>
      )}
    </div>
  );
}

interface ResinViewProps {
  picker: ReactNode;
  group: ResinFamilyGroup;
  selectedBaseCode: string | null;
  searchText: string;
  onSelectBaseCode: (baseCode: string | null) => void;
  onSearch: (searchText: string) => void;
  readsAsByCode: ReturnType<typeof readsAsLookup>;
  canWrite: boolean;
  onTdsChanged: () => void;
}

function ResinView({
  picker,
  group,
  selectedBaseCode,
  searchText,
  onSelectBaseCode,
  onSearch,
  readsAsByCode,
  canWrite,
  onTdsChanged,
}: ResinViewProps) {
  const resinLabel = group.resinFamilyCode === NO_RESIN_FAMILY ? group.displayName : group.resinFamilyCode;
  const visibleDesignations = useMemo(
    () => filterDesignations(filterByBaseCode(group.designations, selectedBaseCode), searchText),
    [group, selectedBaseCode, searchText],
  );
  const designationCountByBase = useMemo(() => designationCountByBaseCode(group), [group]);
  const [openDesignationIds, setOpenDesignationIds] = useState<Set<number>>(new Set());
  const visibleGradeCount = visibleDesignations.reduce((total, item) => total + item.gradeLinks.length, 0);
  const isEverythingOpen =
    visibleDesignations.length > 0 &&
    visibleDesignations.every(({ designation }) => openDesignationIds.has(designation.designationId));
  const emptyText = searchText.trim()
    ? `Nothing matches "${searchText.trim()}".`
    : selectedBaseCode
      ? `No RS codes use ${selectedBaseCode} yet.`
      : "No RS codes saved for this resin yet.";

  const toggleDesignation = (designationId: number) =>
    setOpenDesignationIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(designationId)) nextIds.delete(designationId);
      else nextIds.add(designationId);
      return nextIds;
    });

  const toggleAllDesignations = () =>
    setOpenDesignationIds(
      isEverythingOpen ? new Set() : new Set(visibleDesignations.map(({ designation }) => designation.designationId)),
    );

  const search = (nextSearchText: string) => {
    onSearch(nextSearchText);
    const matchingDesignations = nextSearchText.trim()
      ? filterDesignations(filterByBaseCode(group.designations, selectedBaseCode), nextSearchText)
      : [];
    setOpenDesignationIds(new Set(matchingDesignations.map(({ designation }) => designation.designationId)));
  };

  return (
    <>
      <div className={TOOLBAR_CLASSES}>
        {picker}
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="m-0 text-base font-semibold text-white">{resinLabel}</h3>
          {group.resinFamilyCode !== NO_RESIN_FAMILY && group.displayName !== group.resinFamilyCode && (
            <span className="text-[13px] text-[#a9b0a1]">{group.displayName}</span>
          )}
          <Chip variant={group.designations.length ? "ok" : "default"}>{plural(group.designations.length, "RS code")}</Chip>
          <Chip variant={group.gradeCount ? "ok" : "default"}>{plural(group.gradeCount, "grade")}</Chip>
          <Chip variant={group.baseCodes.length ? "ok" : "default"}>{plural(group.baseCodes.length, "base code")}</Chip>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {group.baseCodes.length > 0 && (
            <BaseCodeSelect
              resinLabel={resinLabel}
              baseCodes={group.baseCodes}
              designationCountByBase={designationCountByBase}
              totalDesignationCount={group.designations.length}
              selectedBaseCode={selectedBaseCode}
              onSelect={onSelectBaseCode}
            />
          )}
          <input
            className="ctl max-w-90 text-[13px] py-1.5"
            placeholder="Search RS code, Reads As, part, grade, supplier…"
            aria-label={`Search ${resinLabel}`}
            value={searchText}
            onChange={(event) => search(event.target.value)}
          />
          <span className="text-[12.5px] text-[#a9b0a1]">
            {plural(visibleDesignations.length, "RS code")} · {plural(visibleGradeCount, "grade")}
          </span>
          {visibleDesignations.length > 0 && (
            <button
              type="button"
              className="bg-transparent border-0 p-0 text-[12.5px] text-rs-green cursor-pointer hover:underline"
              onClick={toggleAllDesignations}
            >
              {isEverythingOpen ? "Collapse all" : "Expand all"}
            </button>
          )}
        </div>
      </div>
      <ResinTable
        designations={visibleDesignations}
        emptyText={emptyText}
        openDesignationIds={openDesignationIds}
        onToggleDesignation={toggleDesignation}
        readsAsByCode={readsAsByCode}
        canWrite={canWrite}
        onTdsChanged={onTdsChanged}
      />
    </>
  );
}
